import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import {
  Lock,
  RefreshCw,
  Trash2,
  FileText,
  Users,
  Image,
  BookOpen,
  Quote as QuoteIcon,
  Calendar as CalendarIcon,
  LogOut,
  KeyRound,
  X,
  Save,
} from "lucide-react";
import { api, authApi, getToken, setToken, clearToken } from "@/lib/api";
import AdminTeam from "@/components/admin/AdminTeam";
import AdminGallery from "@/components/admin/AdminGallery";
import AdminStory from "@/components/admin/AdminStory";
import AdminTestimonials from "@/components/admin/AdminTestimonials";
import AdminCalendar from "@/components/admin/AdminCalendar";

const STATUS_OPTIONS = ["new", "contacted", "done", "archived"];

export default function Admin() {
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [usernameInput, setUsernameInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quotesLoaded, setQuotesLoaded] = useState(false);
  const [tab, setTab] = useState("quotes");

  const [changingPw, setChangingPw] = useState(false);

  // Check existing session on first render
  if (!authChecked) {
    setAuthChecked(true);
    const token = getToken();
    if (token) {
      authApi()
        .get("/auth/me")
        .then((res) => setUser(res.data))
        .catch(() => clearToken());
    }
  }

  const fetchQuotes = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await authApi().get("/quotes");
      setQuotes(res.data);
      setQuotesLoaded(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        setUser(null);
      } else {
        setErrorMsg(err?.message || "Error");
      }
    } finally {
      setLoading(false);
    }
  };

  if (user && !quotesLoaded && !loading) {
    fetchQuotes();
  }

  const onLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setErrorMsg("");
    try {
      const res = await api.post("/auth/login", {
        username: usernameInput.trim().toLowerCase(),
        password: passwordInput,
      });
      setToken(res.data.token);
      setUser(res.data.user);
      setUsernameInput("");
      setPasswordInput("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        typeof detail === "string" ? detail : t.admin.bad_key
      );
    } finally {
      setAuthLoading(false);
    }
  };

  const onLogout = () => {
    clearToken();
    setUser(null);
    setQuotes([]);
    setQuotesLoaded(false);
    setTab("quotes");
  };

  const updateStatus = async (id, status) => {
    try {
      await authApi().patch(`/quotes/${id}`, { status });
      setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
    } catch (err) {
      setErrorMsg(err?.message || "Error");
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Radera?")) return;
    try {
      await authApi().delete(`/quotes/${id}`);
      setQuotes((prev) => prev.filter((q) => q.id !== id));
    } catch (err) {
      setErrorMsg(err?.message || "Error");
    }
  };

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("sv-SE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  // ===== LOGIN SCREEN =====
  if (!user) {
    return (
      <div
        data-testid="admin-locked"
        className="pt-28 md:pt-36 px-6 md:px-12 lg:px-16 min-h-screen"
      >
        <div className="text-xs tracking-[0.3em] uppercase text-[#ff5a00] font-mono mb-4">
          / ADMIN
        </div>
        <h1 className="font-display text-6xl sm:text-7xl uppercase leading-none">
          {t.admin.title}
        </h1>

        <form
          onSubmit={onLogin}
          className="mt-12 max-w-md border border-zinc-800 bg-[#0d0d10] p-8"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-2 text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 mb-6">
            <Lock size={14} />
            {t.admin.key_label}
          </div>

          <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block mt-2">
            {t.admin.username_label}
          </label>
          <input
            type="text"
            data-testid="admin-username-input"
            className="brut-input"
            placeholder={t.admin.username_placeholder}
            value={usernameInput}
            onChange={(e) => setUsernameInput(e.target.value)}
            autoFocus
            autoComplete="username"
          />

          <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block mt-4">
            {t.admin.password_label}
          </label>
          <input
            type="password"
            data-testid="admin-password-input"
            className="brut-input"
            placeholder={t.admin.password_placeholder}
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            autoComplete="current-password"
          />

          {errorMsg && (
            <div
              data-testid="admin-login-error"
              className="text-red-400 text-sm mt-4"
            >
              {errorMsg}
            </div>
          )}
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={authLoading || !usernameInput || !passwordInput}
            className="mt-6 w-full bg-[#ff5a00] text-black px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold hover:bg-[#ff7a30] disabled:opacity-60"
          >
            {authLoading ? t.common.loading : t.admin.login_btn}
          </button>
        </form>
      </div>
    );
  }

  // ===== AUTHENTICATED VIEW =====
  const statusColors = {
    new: "bg-[#ff5a00] text-black",
    contacted: "bg-yellow-500 text-black",
    done: "bg-emerald-500 text-black",
    archived: "bg-zinc-700 text-zinc-300",
  };

  const tabs = [
    { key: "quotes", label: t.admin.tabs.quotes, icon: FileText },
    { key: "team", label: t.admin.tabs.team, icon: Users },
    { key: "gallery", label: t.admin.tabs.gallery, icon: Image },
    { key: "story", label: t.admin.tabs.story, icon: BookOpen },
    { key: "testimonials", label: t.admin.tabs.testimonials, icon: QuoteIcon },
    { key: "calendar", label: t.admin.tabs.calendar, icon: CalendarIcon },
  ];

  return (
    <div data-testid="admin-page" className="pt-28 md:pt-36 pb-16">
      <section className="px-6 md:px-12 lg:px-16">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#ff5a00] font-mono mb-4">
              / ADMIN
            </div>
            <h1 className="font-display text-5xl sm:text-6xl uppercase leading-none">
              Bränninger CMS
            </h1>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">
              {t.admin.logged_in_as}
            </div>
            <div
              className="font-display text-xl uppercase mt-1"
              data-testid="admin-user-name"
            >
              {user.name || user.username}
            </div>
            <div className="flex gap-2 mt-3 justify-end">
              <button
                onClick={() => setChangingPw(true)}
                data-testid="admin-change-password-btn"
                className="inline-flex items-center gap-1.5 border border-zinc-700 text-white px-3 py-1.5 text-[10px] tracking-widest uppercase hover:border-[#ff5a00] hover:text-[#ff5a00]"
              >
                <KeyRound size={11} /> {t.admin.change_password}
              </button>
              <button
                onClick={onLogout}
                data-testid="admin-logout-btn"
                className="inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-400 px-3 py-1.5 text-[10px] tracking-widest uppercase hover:border-red-500 hover:text-red-400"
              >
                <LogOut size={11} /> {t.admin.logout}
              </button>
            </div>
          </div>
        </div>

        <div
          className="mt-10 flex flex-wrap gap-0 border-b border-zinc-800"
          data-testid="admin-tabs"
        >
          {tabs.map((tabItem) => {
            const Icon = tabItem.icon;
            const active = tab === tabItem.key;
            return (
              <button
                key={tabItem.key}
                onClick={() => setTab(tabItem.key)}
                data-testid={`admin-tab-${tabItem.key}`}
                className={`inline-flex items-center gap-2 px-5 py-4 text-xs tracking-[0.2em] uppercase border-b-2 -mb-px transition-colors ${
                  active
                    ? "border-[#ff5a00] text-white"
                    : "border-transparent text-zinc-500 hover:text-zinc-300"
                }`}
              >
                <Icon size={14} />
                {tabItem.label}
                {tabItem.key === "quotes" && (
                  <span
                    className={`ml-1 px-1.5 text-[10px] font-mono ${
                      active ? "bg-[#ff5a00] text-black" : "bg-zinc-800 text-zinc-400"
                    }`}
                  >
                    {quotes.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 mt-10">
        {errorMsg && tab === "quotes" && (
          <div className="mb-4 border border-red-600 bg-red-600/10 p-3 text-sm text-red-300">
            {errorMsg}
          </div>
        )}

        {tab === "quotes" && (
          <>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <h2 className="font-display text-3xl md:text-4xl uppercase">
                {t.admin.title}
              </h2>
              <button
                onClick={fetchQuotes}
                data-testid="admin-refresh-btn"
                className="inline-flex items-center gap-2 border border-zinc-700 text-white px-5 py-3 text-xs tracking-[0.2em] uppercase hover:border-[#ff5a00] hover:text-[#ff5a00] transition-colors"
              >
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                Uppdatera
              </button>
            </div>

            {quotes.length === 0 ? (
              <div
                data-testid="admin-empty"
                className="border border-zinc-800 bg-[#0d0d10] p-16 text-center text-zinc-500"
              >
                {t.admin.empty}
              </div>
            ) : (
              <div className="border border-zinc-800 overflow-x-auto">
                <table
                  data-testid="admin-quotes-table"
                  className="w-full text-sm border-collapse"
                >
                  <thead>
                    <tr className="border-b border-zinc-800 bg-[#0d0d10] text-left">
                      <th className="p-4 text-xs font-mono tracking-widest uppercase text-zinc-500">
                        {t.admin.columns.date}
                      </th>
                      <th className="p-4 text-xs font-mono tracking-widest uppercase text-zinc-500">
                        {t.admin.columns.name}
                      </th>
                      <th className="p-4 text-xs font-mono tracking-widest uppercase text-zinc-500">
                        {t.admin.columns.contact}
                      </th>
                      <th className="p-4 text-xs font-mono tracking-widest uppercase text-zinc-500">
                        {t.admin.columns.service}
                      </th>
                      <th className="p-4 text-xs font-mono tracking-widest uppercase text-zinc-500">
                        {t.admin.columns.details}
                      </th>
                      <th className="p-4 text-xs font-mono tracking-widest uppercase text-zinc-500">
                        {t.admin.columns.status}
                      </th>
                      <th className="p-4 text-xs font-mono tracking-widest uppercase text-zinc-500 text-right">
                        {t.admin.columns.actions}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map((q) => (
                      <tr
                        key={q.id}
                        data-testid={`admin-quote-row-${q.id}`}
                        className="border-b border-zinc-800 hover:bg-zinc-900/50 align-top"
                      >
                        <td className="p-4 font-mono text-xs text-zinc-400 whitespace-nowrap">
                          {formatDate(q.created_at)}
                        </td>
                        <td className="p-4">
                          <div className="text-white">{q.name}</div>
                          {q.company && (
                            <div className="text-xs text-zinc-500 mt-1">
                              {q.company}
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-zinc-300">
                          <a
                            href={`mailto:${q.email}`}
                            className="block hover:text-[#ff5a00]"
                          >
                            {q.email}
                          </a>
                          <a
                            href={`tel:${q.phone}`}
                            className="block text-xs text-zinc-500 mt-1 hover:text-[#ff5a00]"
                          >
                            {q.phone}
                          </a>
                        </td>
                        <td className="p-4">
                          <span className="border border-[#ff5a00] text-[#ff5a00] px-2 py-1 text-xs tracking-widest uppercase font-mono">
                            {t.quote.services[q.service_type] || q.service_type}
                          </span>
                        </td>
                        <td className="p-4 text-zinc-300 max-w-xs">
                          {q.pickup && (
                            <div className="text-xs">
                              <span className="text-zinc-500">
                                {t.quote.fields.pickup}:{" "}
                              </span>
                              {q.pickup}
                            </div>
                          )}
                          {q.delivery && (
                            <div className="text-xs">
                              <span className="text-zinc-500">
                                {t.quote.fields.delivery}:{" "}
                              </span>
                              {q.delivery}
                            </div>
                          )}
                          {q.pickup_date && (
                            <div className="text-xs">
                              <span className="text-zinc-500">
                                {t.quote.fields.date}:{" "}
                              </span>
                              {q.pickup_date}
                            </div>
                          )}
                          {q.cargo && (
                            <div className="text-xs">
                              <span className="text-zinc-500">
                                {t.quote.fields.cargo}:{" "}
                              </span>
                              {q.cargo}
                            </div>
                          )}
                          {q.message && (
                            <div className="text-xs mt-2 text-zinc-400 italic line-clamp-3">
                              “{q.message}”
                            </div>
                          )}
                        </td>
                        <td className="p-4">
                          <select
                            data-testid={`admin-status-select-${q.id}`}
                            value={q.status}
                            onChange={(e) => updateStatus(q.id, e.target.value)}
                            className={`text-xs uppercase tracking-widest font-bold px-2 py-1 border-0 cursor-pointer ${
                              statusColors[q.status] || "bg-zinc-700 text-white"
                            }`}
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option
                                key={s}
                                value={s}
                                className="bg-black text-white"
                              >
                                {t.admin.statuses[s]}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => deleteQuote(q.id)}
                            data-testid={`admin-delete-${q.id}`}
                            className="text-zinc-500 hover:text-red-400 transition-colors"
                            title={t.admin.delete}
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === "team" && <AdminTeam adminKey={null} />}
        {tab === "gallery" && <AdminGallery adminKey={null} />}
        {tab === "story" && <AdminStory adminKey={null} />}
        {tab === "testimonials" && <AdminTestimonials adminKey={null} />}
        {tab === "calendar" && <AdminCalendar adminKey={null} />}
      </section>

      {changingPw && (
        <ChangePasswordModal onClose={() => setChangingPw(false)} t={t} />
      )}
    </div>
  );
}

const ChangePasswordModal = ({ onClose, t }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next !== confirm) {
      setErr(t.admin.change_password_mismatch);
      return;
    }
    setSaving(true);
    try {
      await authApi().post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      setOk(true);
      setCurrent("");
      setNext("");
      setConfirm("");
      setTimeout(onClose, 1500);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Kunde inte byta lösenord");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="change-password-modal"
    >
      <form
        onSubmit={submit}
        className="bg-[#0d0d10] border border-zinc-800 max-w-md w-full"
      >
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <div className="font-display text-2xl uppercase flex items-center gap-2">
            <KeyRound size={18} className="text-[#ff5a00]" />
            {t.admin.change_password}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
              {t.admin.change_password_current}
            </label>
            <input
              type="password"
              data-testid="change-password-current"
              className="brut-input"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
              {t.admin.change_password_new}
            </label>
            <input
              type="password"
              data-testid="change-password-new"
              className="brut-input"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={6}
              required
            />
          </div>
          <div>
            <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
              {t.admin.change_password_confirm}
            </label>
            <input
              type="password"
              data-testid="change-password-confirm"
              className="brut-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={6}
              required
            />
          </div>

          {err && <div className="text-sm text-red-400">{err}</div>}
          {ok && (
            <div className="text-sm text-emerald-400">
              ✓ {t.admin.change_password_success}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-zinc-800 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="border border-zinc-700 text-white px-5 py-3 text-xs tracking-widest uppercase hover:border-zinc-500"
          >
            Avbryt
          </button>
          <button
            type="submit"
            data-testid="change-password-save-btn"
            disabled={saving || !current || !next || !confirm}
            className="inline-flex items-center gap-2 bg-[#ff5a00] text-black px-5 py-3 text-xs tracking-widest uppercase font-bold hover:bg-[#ff7a30] disabled:opacity-50"
          >
            <Save size={14} /> {t.admin.change_password_save}
          </button>
        </div>
      </form>
    </div>
  );
};
